import { type SortDirection } from '@tanstack/react-table';
import { enUS, es, ca, type Locale } from 'date-fns/locale';
import CataloniaFlag from '@avanta/shared/assets/flags/ca.svg';
import UnitedKingdomFlag from '@avanta/shared/assets/flags/en.svg';
import SpainFlag from '@avanta/shared/assets/flags/es.svg';
import { Languages } from './languages';

export { Languages };

export const BASE_URL = import.meta.env.VITE_API_URL;
export const AUTHORIZATION = import.meta.env.VITE_AUTHORIZATION;
export const ANONYMOUS_USER = import.meta.env.VITE_ANONYMOUS_USER;
export const ANONYMOUS_PASSWORD = import.meta.env.VITE_ANONYMOUS_PASSWORD;
export const OAUTH2_CLIENT_ID = import.meta.env.VITE_OAUTH2_CLIENT_ID;
export const OAUTH2_SECRET_ID = import.meta.env.VITE_OAUTH2_SECRET_ID;

export const APPOINTMENTS_SCHEDULE_DAYS =
  Number(import.meta.env.VITE_APPOINTMENTS_SCHEDULE_DAYS) || 21;

export const FIFTEEN_MINUTES = 1000 * 60 * 15;
export const AM_XL_WIDTH_PX = 768;

export const MODES = {
  primary: 'primary',
  secondary: 'secondary',
} as const;

export const LOCALES: Record<Languages, Locale> = {
  es,
  en: enUS,
  ca,
};

export const DEFAULT_TIMEZONE = 'Europe/Madrid';
export const DEFAULT_LANGUAGE = Languages.Spanish;

/** App-wide time display mask (date-fns), e.g. 09h30 */
export const TIME_DISPLAY_FORMAT = "HH'h'mm";
export const DATETIME_DISPLAY_FORMAT = `dd/MM/yyyy ${TIME_DISPLAY_FORMAT}`;

export const LOCIZE_NS = {
  COMMON: 'common',
  AUTH: 'auth',
  APPOINTMENTS: 'appointments',
  CHAT: 'chat',
  HOME: 'home',
  INSURANCE: 'insurance',
  WELL_BEING: 'well-being',
  PROFILE: 'profile',
  SERVICES: 'services',
  PSYCHOLOGIST: 'psychologist',
} as const;

export const QUERY_KEYS = {
  FETCH_ADMINS: 'FETCH_ADMINS',
  FETCH_ADMINS_COUNT: 'FETCH_ADMINS_COUNT',
  FETCH_APPOINTMENTS_HISTORY: 'FETCH_APPOINTMENTS_HISTORY',
  FETCH_CENTER_DETAILS: 'FETCH_CENTER_DETAILS',
  FETCH_CENTERS: 'FETCH_CENTERS',
  FETCH_CENTERS_BY_ENTERPRISE: 'FETCH_CENTERS_BY_ENTERPRISE',
  FETCH_COUPONS: 'FETCH_COUPONS',
  FETCH_DEPARTMENTS: 'FETCH_DEPARTMENTS',
  FETCH_ENTERPRISE_APPOINTMENTS: 'FETCH_ENTERPRISE_APPOINTMENTS',
  FETCH_ENTERPRISE_ACTIVITY: 'FETCH_ENTERPRISE_ACTIVITY',
  FETCH_FREE_HOURS_AVAILABILITY: 'FETCH_FREE_HOURS_AVAILABILITY',
  FETCH_ENTERPRISE_INVOICES: 'FETCH_ENTERPRISE_INVOICES',
  FETCH_INSURED_USERS: 'FETCH_INSURED_USERS',
  FETCH_INSURED_USERS_COUNT: 'FETCH_INSURED_USERS_COUNT',
  FETCH_NEXT_APPOINTMENTS: 'FETCH_NEXT_APPOINTMENTS',
  FETCH_PATIENT: 'FETCH_PATIENT',
  FETCH_PSYCHOLOGIST_DETAILS: 'FETCH_PSYCHOLOGIST_DETAILS',
  FETCH_PSYCHOLOGISTS_BY_NAME: 'FETCH_PSYCHOLOGISTS_BY_NAME',
  FETCH_REPORTS_HISTORY: 'FETCH_REPORTS_HISTORY',
  FETCH_SPECIALITIES: 'FETCH_SPECIALITIES',
  FETCH_SPECIALITIES_BY_PSYCHO: 'FETCH_SPECIALITIES_BY_PSYCHO',
  FETCH_TOP_PSYCHOLOGISTS_AVAILABILITY: 'FETCH_TOP_PSYCHOLOGISTS_AVAILABILITY',
  FETCH_USER: 'FETCH_USER',
} as const;

export const MUTATION_KEYS = {
  BOOK_APPOINTMENT: 'BOOK_APPOINTMENT',
  UPDATE_APPOINTMENT: 'UPDATE_APPOINTMENT',
  BAN_ADMIN: 'BAN_ADMIN',
  CANCEL_APPOINTMENT: 'CANCEL_APPOINTMENT',
  CREATE_ADMIN: 'CREATE_ADMIN',
  CREATE_INSURED_PERSON: 'CREATE_INSURED_PERSON',
  DELETE_ADMIN: 'DELETE_ADMIN',
  DELETE_PATIENT: 'DELETE_PATIENT',
  DOWNLOAD_ENTERPRISE_INVOICE_PDF: 'DOWNLOAD_ENTERPRISE_INVOICE_PDF',
  DOWNLOAD_ENTERPRISE_SESSION_REPORT: 'DOWNLOAD_ENTERPRISE_SESSION_REPORT',
  UPDATE_EMPLOYEE: 'UPDATE_EMPLOYEE',
  UPDATE_PATIENT: 'UPDATE_PATIENT',
} as const;

export const languages = {
  [Languages.Spanish]: {
    value: Languages.Spanish,
    label: `${LOCIZE_NS.COMMON}:Common.languages.es`,
    image: SpainFlag,
    shortLabel: 'ESP',
  },
  [Languages.English]: {
    value: Languages.English,
    label: `${LOCIZE_NS.COMMON}:Common.languages.en`,
    image: UnitedKingdomFlag,
    shortLabel: 'ENG',
  },
  [Languages.Catalan]: {
    value: Languages.Catalan,
    label: `${LOCIZE_NS.COMMON}:Common.languages.ca`,
    image: CataloniaFlag,
    shortLabel: 'CAT',
  },
};

export const SORT_DIRECTION = {
  asc: 'asc',
  desc: 'desc',
} as const satisfies Record<SortDirection, SortDirection>;
